import React, { useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  IconButton,
  InputAdornment,
} from '@mui/material'
import {
  Close,
  Save,
  Cancel,
} from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useAppDispatch } from '../../hooks/redux'
import { createOrder, updateOrder } from '../../store/slices/ordersSlice'

const orderSchema = yup.object({
  order_number: yup.string().required('Order number is required'),
  customer_name: yup.string().required('Customer name is required'),
  product_type: yup.string().required('Product type is required'),
  quantity: yup.number().required('Quantity is required').min(1, 'Quantity must be at least 1'),
  unit_price: yup.number().required('Unit price is required').min(0, 'Unit price must be positive'),
  due_date: yup.date().required('Due date is required'),
  priority: yup.string().required('Priority is required'),
  status: yup.string().required('Status is required'),
  description: yup.string(),
})

interface OrderFormData {
  order_number: string
  customer_name: string
  product_type: string
  quantity: number
  unit_price: number
  due_date: Date
  priority: string
  status: string
  description?: string
}

interface OrderDialogProps {
  open: boolean
  onClose: () => void
  order?: any
}

const ORDER_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
]

const ORDER_PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
]

const PRODUCT_TYPES = [
  'T-Shirts',
  'Polo Shirts',
  'Dress Shirts',
  'Pants',
  'Jeans',
  'Dresses',
  'Skirts',
  'Jackets',
  'Hoodies',
  'Uniforms',
]

export const OrderDialog: React.FC<OrderDialogProps> = ({
  open,
  onClose,
  order,
}) => {
  const dispatch = useAppDispatch()
  const isEdit = !!order

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<OrderFormData>({
    resolver: yupResolver(orderSchema) as any,
    defaultValues: {
      order_number: '',
      customer_name: '',
      product_type: '',
      quantity: 1,
      unit_price: 0,
      due_date: new Date(),
      priority: 'medium',
      status: 'pending',
      description: '',
    },
  })

  const quantity = watch('quantity')
  const unitPrice = watch('unit_price')
  const totalAmount = quantity * unitPrice

  useEffect(() => {
    if (order) {
      reset({
        order_number: order.order_number,
        customer_name: order.customer_name,
        product_type: order.product_type,
        quantity: order.quantity,
        unit_price: order.unit_price,
        due_date: new Date(order.due_date),
        priority: order.priority,
        status: order.status,
        description: order.description || '',
      })
    } else {
      reset({
        order_number: `ORD-${Date.now()}`,
        customer_name: '',
        product_type: '',
        quantity: 1,
        unit_price: 0,
        due_date: new Date(),
        priority: 'medium',
        status: 'pending',
        description: '',
      })
    }
  }, [order, reset])

  const onSubmit = async (data: OrderFormData) => {
    try {
      // Map frontend data to backend schema
      const priorityMap: { [key: string]: number } = {
        'low': 1,
        'medium': 2,
        'high': 3,
        'urgent': 4
      }

      const orderData = {
        order_number: data.order_number,
        customer_name: data.customer_name,
        product_description: data.product_type, // Map product_type to product_description
        quantity: data.quantity,
        unit_price: data.unit_price,
        due_date: data.due_date.toISOString().split('T')[0], // Convert to date format (YYYY-MM-DD)
        priority_level: priorityMap[data.priority] || 2, // Map priority string to priority_level number
        description: data.description,
      }

      if (isEdit) {
        await dispatch(updateOrder({ orderId: order.order_id, orderData })).unwrap()
      } else {
        await dispatch(createOrder(orderData)).unwrap()
      }

      onClose()
    } catch (error: any) {
      console.error('Failed to save order:', error)
      
      // Show user-friendly error message
      let errorMessage = 'Failed to save order'
      if (error?.message) {
        if (error.message.includes('Cannot update order in')) {
          errorMessage = 'Cannot update this order. Orders that are completed, cancelled, or delivered cannot be modified.'
        } else {
          errorMessage = error.message
        }
      }
      
      // You could show this in a toast notification or alert
      alert(errorMessage)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={window.innerWidth < 600}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: '20px' },
          backgroundColor: '#f5f7fa',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: '90vh' },
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ffa500 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            {isEdit ? 'Edit Order' : 'Create New Order'}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit as any)}>
        <DialogContent
          sx={{
            backgroundColor: '#f5f7fa',
            py: { xs: 2, sm: 3 },
            px: { xs: 2, sm: 3 },
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '10px',
              '&:hover': {
                background: '#555',
              },
            },
            '& .MuiTextField-root': {
              mb: { xs: 2, sm: 3 },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                borderRadius: '16px',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '& fieldset': {
                  borderColor: '#e0e0e0',
                  borderWidth: '2px',
                },
                '&:hover fieldset': {
                  borderColor: '#ff6b6b',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#ff6b6b',
                  borderWidth: 2,
                },
                '&.Mui-error fieldset': {
                  borderColor: 'error.main',
                },
              },
              '& .MuiOutlinedInput-input': {
                py: { xs: 1.5, sm: 2 },
              },
              '& .MuiInputLabel-root': {
                color: 'text.secondary',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '&.Mui-focused': {
                  color: '#ff6b6b',
                },
                '&.Mui-error': {
                  color: 'error.main',
                },
              },
            },
            '& .MuiFormControl-root': {
              mb: { xs: 2, sm: 3 },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                borderRadius: '16px',
                '& fieldset': {
                  borderColor: '#e0e0e0',
                  borderWidth: '2px',
                },
                '&:hover fieldset': {
                  borderColor: '#ff6b6b',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#ff6b6b',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                color: 'text.secondary',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '&.Mui-focused': {
                  color: '#ff6b6b',
                },
              },
            },
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name="order_number"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Order Number"
                    error={!!errors.order_number}
                    helperText={errors.order_number?.message}
                    disabled={isEdit}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="customer_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Customer Name"
                    error={!!errors.customer_name}
                    helperText={errors.customer_name?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="product_type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.product_type}>
                    <InputLabel>Product Type</InputLabel>
                    <Select {...field} label="Product Type">
                      {PRODUCT_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="quantity"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Quantity"
                    type="number"
                    error={!!errors.quantity}
                    helperText={errors.quantity?.message}
                    inputProps={{ min: 1 }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="unit_price"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Unit Price"
                    type="number"
                    error={!!errors.unit_price}
                    helperText={errors.unit_price?.message}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">Rs.</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Total Amount"
                value={`Rs. ${totalAmount.toFixed(2)}`}
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="due_date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    label="Due Date"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.due_date,
                        helperText: errors.due_date?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="priority"
                control={control}
                render={({ field }: any) => (
                  <FormControl fullWidth error={!!errors.priority}>
                    <InputLabel>Priority</InputLabel>
                    <Select {...field} label="Priority">
                      {ORDER_PRIORITIES.map((priority) => (
                        <MenuItem key={priority.value} value={priority.value}>
                          {priority.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="status"
                control={control}
                render={({ field }: any) => (
                  <FormControl fullWidth error={!!errors.status}>
                    <InputLabel>Status</InputLabel>
                    <Select {...field} label="Status">
                      {ORDER_STATUSES.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }: any) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            backgroundColor: 'white',
            borderTop: '2px solid #f0f0f0',
            p: { xs: 2, sm: 3 },
            gap: { xs: 1, sm: 2 },
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            startIcon={<Cancel />}
            disabled={isSubmitting}
            fullWidth
            sx={{
              borderRadius: '16px',
              borderColor: '#e0e0e0',
              borderWidth: '2px',
              color: 'text.primary',
              fontWeight: 600,
              px: { xs: 3, sm: 4 },
              py: { xs: 1.25, sm: 1.5 },
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#bdbdbd',
                borderWidth: '2px',
              },
              '&:disabled': {
                opacity: 0.6,
                borderColor: '#e0e0e0',
                color: 'text.secondary',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Save />}
            disabled={isSubmitting}
            fullWidth
            sx={{
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ffa500 100%)',
              color: 'white',
              fontWeight: 600,
              px: { xs: 3, sm: 4 },
              py: { xs: 1.25, sm: 1.5 },
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #ff5252 0%, #ff9100 100%)',
                boxShadow: '0 6px 16px rgba(255, 107, 107, 0.4)',
              },
              '&:active': {
                boxShadow: '0 2px 8px rgba(255, 107, 107, 0.3)',
              },
              '&:disabled': {
                opacity: 0.6,
                background: '#e0e0e0',
                color: 'text.secondary',
                boxShadow: 'none',
              },
            }}
          >
            {isSubmitting ? 'Saving...' : isEdit ? 'Update Order' : 'Create Order'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
